export type BaseApp = {
  avatarUrl: string;
  id: string;
  isActive?: boolean;
  isGlobal?: boolean;
  name: string;
  systemApp: SystemApp;
};

export type App = BaseApp & {
  callbackUrl: string;
  createdAt: Date;
  events: AppEventType[];
  integration?: Record<string, string>;
  secret: string;
  updatedAt: Date;
  userId: string;
  webhooks: Array<WebhookSetting>;
  workspaceId: string;
};

export type WebhookSetting = {
  callbackUrl: string;
  events: Array<AppEventType>;
  secret?: string;
};

export type CreateAppPayload = {
  avatarUrl: string;
  events: AppEventType[];
  integration?: Record<string, string>;
  isActive?: boolean;
  name: string;
  secret: string;
  systemApp?: SystemApp;
  workspaceId: string;
};

export type UpdateAppPayload = Partial<CreateAppPayload>;

export enum AppEventType {
  ORDER_CREATED = "ORDER_CREATED",
  ORDER_UPDATED = "ORDER_UPDATED",
  ORDER_CANCELLED = "ORDER_CANCELLED",
  ORDER_IN_TRANSIT_TO_PICKUP = "ORDER_IN_TRANSIT_TO_PICKUP",
  ORDER_ARRIVED_AT_PICKUP = "ORDER_ARRIVED_AT_PICKUP",
  ORDER_PICKED_UP = "ORDER_PICKED_UP",
  ORDER_IN_TRANSIT_TO_DROPOFF = "ORDER_IN_TRANSIT_TO_DROPOFF",
  ORDER_ARRIVED_AT_DROPOFF = "ORDER_ARRIVED_AT_DROPOFF",
  ORDER_DROPPED_OFF = "ORDER_DROPPED_OFF",
  ORDER_ASSIGNED = "ORDER_ASSIGNED",
  ORDER_UNASSIGNED = "ORDER_UNASSIGNED",
  ROUTE_CREATED = "ROUTE_CREATED",
  ROUTE_UPDATED = "ROUTE_UPDATED",
}

export enum SystemApp {
  PUBLIC_FORM = "public_form",
  UBER = "uber",
  DOORDASH = "doordash",
  UBER_CATERING = "uber_catering",
  EZCATER = "ezcater",
  ROADIE = "roadie",
  EXPEDITE = "expedite",
  ONFLEET = "onfleet",
  SLACK = "slack",
  NASH = "nash",
  TWILIO_FLEX = "twilioFlex",
  FULFLLD_AI_CHAT = "fulflld_ai_chat",
}

export const INTEGRATION_ATTRIBUTE_DISPLAY_NAMES = {
  [SystemApp["UBER"]]: {
    clientId: "Client ID",
    clientSecret: "Client Secret",
    customerId: "Customer ID",
  },
  [SystemApp["UBER_CATERING"]]: {
    clientId: "Client ID",
    clientSecret: "Client Secret",
    customerId: "Customer ID",
  },
  [SystemApp["DOORDASH"]]: {
    developerId: "Developer ID",
    keyId: "Key ID",
    signingSecret: "Signing Secret",
  },
  [SystemApp["ROADIE"]]: {
    accessToken: "Access Token",
  },
  [SystemApp["EZCATER"]]: {
    authToken: "Auth Token",
  },
  [SystemApp["EXPEDITE"]]: {
    secret: "API Secret",
    token: "API Token",
  },
  [SystemApp["ONFLEET"]]: {
    apiKey: "API Key",
  },
  [SystemApp["SLACK"]]: {
    apiToken: "API Token",
  },
  [SystemApp["NASH"]]: {
    apiToken: "App Secret",
  },
  [SystemApp["TWILIO_FLEX"]]: {
    accountSid: "Account SID",
    authToken: "Auth Token",
    conversationsServiceSid: "Conversations Service SID",
    studioFlowSid: "Studio Flow SID",
    workflowSid: "Workflow SID",
    workspaceSid: "Workspace SID",
  },
} as const;

export const INTEGRATION_INFO: Record<
  string,
  { caption: string; description: string; url: string }
> = {
  [SystemApp["UBER"]]: {
    caption: "Connect with Uber for reliable, real-time ride coordination.",
    description:
      "The Uber integration brings seamless transportation solutions to your platform. Whether you need to offer on-demand rides, schedule trips, or track drivers in real time, Uber's powerful API ensures your users enjoy a smooth and efficient travel experience. Ideal for businesses that prioritize convenience and reliability, this integration simplifies logistics and enhances customer satisfaction.",
    url: "/images/uber.png",
  },
  [SystemApp["DOORDASH"]]: {
    caption: "Streamline food delivery operations with DoorDash integration.",
    description:
      "Integrate DoorDash to transform your food delivery services. From real-time order tracking to effortless syncing with your POS systems, this integration ensures that your customers enjoy fast, accurate, and efficient deliveries. Perfect for restaurants, virtual kitchens, and catering businesses, DoorDash offers the tools to elevate your operations and grow your delivery network.",
    url: "/images/doordash.png",
  },
  [SystemApp["UBER_CATERING"]]: {
    caption: "Simplify bulk food deliveries with Uber Catering.",
    description:
      "Uber Catering makes managing large-scale food orders a breeze. With features tailored for high-volume and scheduled deliveries, this integration is perfect for corporate events, group meals, or special occasions. Enhance your platform with a reliable catering solution that ensures timely service and satisfied customers, every time.",
    url: "/images/uber_eats.png",
  },
  [SystemApp["ROADIE"]]: {
    caption:
      "LAST-MILE DELIVERY SOLUTIONS. Deliver same day. Bigger. Farther. Better.",
    description:
      "Roadie allows businesses to offer exceptional delivery experiences that drive customer satisfaction and loyalty. Deliver bigger items, faster than ever, more reliably with the nation's largest local same-day delivery network.",
    url: "/images/roadie.png",
  },
  [SystemApp["EZCATER"]]: {
    caption: "The best way to order food for work",
    description: "Bring great food to teams of any size, anywhere",
    url: "/images/ezcater.webp",
  },
  [SystemApp["EXPEDITE"]]: {
    caption: "Custom Same-Day Delivery Solutions, Just For You",
    description:
      "Have the competitive edge with our modern logistics technology and professional delivery personnel.",
    url: "/images/expedite.png",
  },
  [SystemApp["ONFLEET"]]: {
    caption: "Last mile delivery management for your own fleet.",
    description:
      "Onfleet powers efficient last mile deliveries with route optimization, real-time tracking, proof of delivery, and analytics. Manage your own drivers with an intuitive platform built for local delivery operations.",
    url: "/images/onfleet.png",
  },
  [SystemApp["SLACK"]]: {
    caption: "Connect your workspace with Slack notifications",
    description:
      "Integrate Slack to receive real-time notifications and updates directly in your team's channels. Stay informed about orders, deliveries, and important events without leaving your workspace. <br> <br> To retrieve the API token for your Slack workspace, please click <a href='https://docs.fulflld.com/docs/slack-integration' target='_blank' rel='noopener noreferrer' style='color: #1a73e8; text-decoration: underline;'>here</a> to proceed.",
    url: "/images/slack.png",
  },
  [SystemApp["NASH"]]: {
    caption: "Connect your workspace with Nash notifications",
    description:
      "Nash is a delivery orchestration platform enabling your business to offer reliable delivery.",
    url: "/images/nash.png",
  },
  [SystemApp["TWILIO_FLEX"]]: {
    caption: "Enhance customer engagement with Twilio Flex",
    description:
      "Integrate Twilio Flex to power your contact center operations with a flexible, programmable platform. Manage conversations, workflows, and customer interactions seamlessly across multiple channels. Built for businesses that need scalable, customizable communication solutions.",
    url: "/images/twilio-flex.png",
  },
} as const;

export type IntegrationName = keyof typeof INTEGRATION_ATTRIBUTE_DISPLAY_NAMES;
